import React, { useCallback, useEffect, useState } from 'react';
import { Input, InputNumber, Modal, Select, Switch } from 'antd';
import { PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
    ActionButton,
    DataTable,
    PageCard,
    PageShell,
    PageToolbar,
    RowIconButton,
    StatusBadge,
} from '../../components/ui';
import {
    createTechnicianObject,
    deleteTechnicianObject,
    fetchTechnicianObjects,
    firstApiError,
    formatTechPrice,
    PAGE_SIZE,
    updateTechnicianObject,
} from '../../services/technicianRequestService';

function emptyParam() {
    return { key: `${Date.now()}-${Math.random()}`, name: '', options: [], price: 0, is_required: true };
}

function toastError(error, t, fallback) {
    const code = error?.response?.status;
    if (code === 403) {
        toast.error(t('techRequest.forbidden'));
        return;
    }
    toast.error(firstApiError(error) || t(fallback));
}

export default function TechnicianObjects() {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: PAGE_SIZE, total: 0 });
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState(0);
    const [isActive, setIsActive] = useState(true);
    const [parameters, setParameters] = useState([emptyParam()]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const result = await fetchTechnicianObjects('admin', {
                page: pagination.current,
                perPage: pagination.pageSize,
            });
            setItems(result.items);
            setPagination((prev) => ({ ...prev, total: result.pagination.total, current: result.pagination.current }));
        } catch (error) {
            setItems([]);
            if (error?.response?.status !== 401) toastError(error, t, 'techRequest.loadError');
        } finally {
            setLoading(false);
        }
    }, [pagination.current, pagination.pageSize, t]);

    useEffect(() => {
        load();
    }, [load]);

    const openCreate = () => {
        setEditing(null);
        setName('');
        setDescription('');
        setPrice(0);
        setIsActive(true);
        setParameters([emptyParam()]);
        setModalOpen(true);
    };

    const openEdit = (item) => {
        setEditing(item);
        setName(item.name || '');
        setDescription(item.description || '');
        setPrice(Number(item.price || 0));
        setIsActive(item.is_active !== false);
        setParameters(
            (item.parameters || []).map((p) => ({
                key: `p-${p.id}`,
                id: p.id,
                name: p.name,
                options: p.options || [],
                price: Number(p.price || 0),
                is_required: p.is_required !== false,
            }))
        );
        setModalOpen(true);
    };

    const updateParam = (key, patch) => {
        setParameters((prev) => prev.map((p) => (p.key === key ? { ...p, ...patch } : p)));
    };

    const buildPayload = (includeParameters) => {
        const body = {
            name: name.trim(),
            description: description.trim() || null,
            price: Number(price).toFixed(2),
            is_active: isActive,
        };
        if (includeParameters) {
            body.parameters = parameters.map((p) => {
                const row = {
                    name: p.name.trim(),
                    options: [...new Set((p.options || []).map((o) => String(o).trim()).filter(Boolean))],
                    price: Number(p.price || 0).toFixed(2),
                    is_required: Boolean(p.is_required),
                };
                if (p.id) row.id = p.id;
                return row;
            });
        }
        return body;
    };

    const handleSave = async () => {
        if (!name.trim()) {
            toast.error(t('common.required'));
            return;
        }
        if (!editing && !parameters.length) {
            toast.error(t('techRequest.addParameter'));
            return;
        }
        if (parameters.some((p) => !p.name.trim() || !(p.options || []).length)) {
            toast.error(t('techRequest.optionsRequired'));
            return;
        }
        setSaving(true);
        try {
            const body = buildPayload(true);
            if (editing) {
                await updateTechnicianObject(editing.id, body);
            } else {
                await createTechnicianObject(body);
            }
            toast.success(t('techRequest.savedOk'));
            setModalOpen(false);
            load();
        } catch (error) {
            toastError(error, t, 'techRequest.saveError');
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (item) => {
        try {
            await updateTechnicianObject(item.id, { is_active: !item.is_active });
            toast.success(t('techRequest.savedOk'));
            load();
        } catch (error) {
            toastError(error, t, 'techRequest.saveError');
        }
    };

    const handleDelete = (item) => {
        Modal.confirm({
            title: t('common.delete'),
            content: t('techRequest.deleteConfirm'),
            okText: t('common.delete'),
            cancelText: t('common.cancel'),
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await deleteTechnicianObject(item.id);
                    toast.success(t('techRequest.deletedOk'));
                    load();
                } catch (error) {
                    toastError(error, t, 'techRequest.saveError');
                }
            },
        });
    };

    const columns = [
        {
            title: t('common.name'),
            dataIndex: 'name',
            render: (text, record) => (
                <div className="min-w-0">
                    <span className="block text-sm font-medium text-[#1D2C7B]">{text}</span>
                    {record.description && (
                        <span className="mt-0.5 block text-xs text-slate-500 line-clamp-1">{record.description}</span>
                    )}
                </div>
            ),
        },
        {
            title: t('common.price'),
            dataIndex: 'price',
            width: 140,
            render: (value) => (
                <span className="tabular-nums text-sm text-slate-700">
                    {formatTechPrice(value)} {t('common.uzs')}
                </span>
            ),
        },
        {
            title: t('techRequest.parameters'),
            dataIndex: 'parameters',
            width: 90,
            render: (list) => <span className="tabular-nums text-sm text-slate-600">{list?.length ?? 0}</span>,
        },
        {
            title: t('common.status'),
            dataIndex: 'is_active',
            width: 120,
            render: (active, record) => (
                <button type="button" onClick={() => handleToggle(record)}>
                    <StatusBadge tone={active ? 'green' : 'muted'}>
                        {active ? t('techRequest.active') : t('techRequest.inactive')}
                    </StatusBadge>
                </button>
            ),
        },
        {
            title: t('common.actions'),
            key: 'actions',
            width: 88,
            render: (_, record) => (
                <div className="flex items-center gap-0.5">
                    <RowIconButton onClick={() => openEdit(record)} aria-label={t('common.edit')}>
                        <PencilSquareIcon className="h-[18px] w-[18px]" />
                    </RowIconButton>
                    <RowIconButton onClick={() => handleDelete(record)} aria-label={t('common.delete')}>
                        <TrashIcon className="h-[18px] w-[18px]" />
                    </RowIconButton>
                </div>
            ),
        },
    ];

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={t('techRequest.catalogTitle')}
                    count={pagination.total}
                    actions={
                        <ActionButton icon={<PlusIcon className="h-4 w-4" />} onClick={openCreate}>
                            {t('techRequest.catalogNew')}
                        </ActionButton>
                    }
                />
                <DataTable
                    columns={columns}
                    dataSource={items}
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: false,
                    }}
                    onChange={(pager) => setPagination((prev) => ({ ...prev, current: pager.current }))}
                    scrollX={720}
                    mobileCard={(record) => (
                        <div className="px-1 py-1">
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <span className="block text-sm font-semibold text-[#1D2C7B]">{record.name}</span>
                                    <span className="mt-1 block text-xs tabular-nums text-slate-500">
                                        {formatTechPrice(record.price)} {t('common.uzs')}
                                    </span>
                                </div>
                                <StatusBadge tone={record.is_active ? 'green' : 'muted'}>
                                    {record.is_active ? t('techRequest.active') : t('techRequest.inactive')}
                                </StatusBadge>
                            </div>
                            <div className="mt-2 flex gap-2">
                                <button type="button" className="text-xs font-semibold text-[#1D2C7B]" onClick={() => openEdit(record)}>
                                    {t('common.edit')}
                                </button>
                                <button type="button" className="text-xs text-slate-500" onClick={() => handleDelete(record)}>
                                    {t('common.delete')}
                                </button>
                            </div>
                        </div>
                    )}
                />
            </PageCard>

            <Modal
                title={editing ? t('techRequest.catalogEdit') : t('techRequest.catalogNew')}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={handleSave}
                confirmLoading={saving}
                okText={t('common.save')}
                cancelText={t('common.cancel')}
                width={720}
                destroyOnClose
            >
                <div className="space-y-3 pt-1">
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">{t('common.name')}</label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">{t('common.remark')}</label>
                        <Input.TextArea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={2}
                            maxLength={5000}
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">{t('techRequest.objectPrice')}</label>
                            <InputNumber
                                className="!w-full"
                                min={0}
                                max={9999999999.99}
                                precision={2}
                                value={price}
                                onChange={(v) => setPrice(v ?? 0)}
                            />
                        </div>
                        <div className="flex items-end pb-1">
                            <Switch checked={isActive} onChange={setIsActive} />
                            <span className="ml-2 text-sm text-slate-600">{t('techRequest.active')}</span>
                        </div>
                    </div>

                    <div className="flex items-center justify-between pt-1">
                        <span className="text-sm font-semibold text-slate-800">{t('techRequest.parameters')}</span>
                        <button
                            type="button"
                            className="text-xs font-semibold text-[#1D2C7B]"
                            onClick={() => setParameters((prev) => [...prev, emptyParam()])}
                        >
                            {t('techRequest.addParameter')}
                        </button>
                    </div>

                    <div className="space-y-3">
                        {parameters.map((param) => (
                            <div key={param.key} className="rounded-lg border border-slate-200 p-3">
                                <div className="mb-2 flex justify-end">
                                    {parameters.length > 1 && (
                                        <button
                                            type="button"
                                            className="text-xs text-slate-400 hover:text-red-500"
                                            onClick={() => setParameters((prev) => prev.filter((p) => p.key !== param.key))}
                                        >
                                            {t('common.delete')}
                                        </button>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                    <Input
                                        placeholder={t('techRequest.paramName')}
                                        value={param.name}
                                        maxLength={120}
                                        onChange={(e) => updateParam(param.key, { name: e.target.value })}
                                    />
                                    <InputNumber
                                        className="!w-full"
                                        min={0}
                                        max={9999999999.99}
                                        precision={2}
                                        placeholder={t('techRequest.paramPrice')}
                                        value={param.price}
                                        onChange={(v) => updateParam(param.key, { price: v ?? 0 })}
                                    />
                                </div>
                                <Select
                                    className="mt-2 w-full"
                                    mode="tags"
                                    placeholder={t('techRequest.options')}
                                    value={param.options}
                                    onChange={(options) => updateParam(param.key, { options })}
                                />
                                <label className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                                    <Switch
                                        size="small"
                                        checked={param.is_required}
                                        onChange={(v) => updateParam(param.key, { is_required: v })}
                                    />
                                    {t('techRequest.requiredParam')}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            </Modal>
        </PageShell>
    );
}
